import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { routes } from "@bezoned/shared/routes.js";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Card,
  Spinner,
  Title3,
} from "@fluentui/react-components";
import { useLocation, useNavigate } from "react-router-dom";

import { FluidRecreationError } from "../../features/fluid/fluid.errors.js";
import { updateSearchParams } from "../../utils/url-search-params.js";

interface FluidErrorScreenProps {
  error: Error;
  onRetry: () => void;
  isRetrying: boolean;
}

export const FluidErrorScreen = ({
  error,
  onRetry,
  isRetrying,
}: FluidErrorScreenProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const errorMessage =
    error instanceof FluidRecreationError
      ? "We encountered an issue while setting up your office. This can happen when the office structure is being updated."
      : "We couldn't connect to your office. Please check your internet connection and try again.";
  const handleClick77 = useStableCallback(() =>
    navigate(
      `${routes.protected.chooseTeam}${updateSearchParams(location.search, {
        remove: ["teamId"],
      })}`
    )
  );

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gray-50 p-4">
      <Card className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <Title3 className="mb-3">Unable to Load Office</Title3>
          <p className="mb-4 text-[14px] leading-6 text-[#4b5563]">
            {errorMessage}
          </p>

          {/* Details accordion */}
          <div className="mb-4">
            <Accordion collapsible>
              <AccordionItem value="1">
                <AccordionHeader>Show technical details</AccordionHeader>
                <AccordionPanel>
                  <div className="rounded border border-gray-300 bg-gray-50 p-3">
                    <p className="mb-2 text-[12px] font-semibold text-gray-700">
                      Error message:
                    </p>
                    <p className="mb-3 font-mono text-[11px] text-gray-800">
                      {error.message || error.toString() || "Unknown error"}
                    </p>
                    {Boolean(error.stack) && (
                      <>
                        <p className="mb-2 text-[12px] font-semibold text-gray-700">
                          Stack trace:
                        </p>
                        <pre className="overflow-x-auto font-mono text-[10px] break-words whitespace-pre-wrap text-gray-600">
                          {error.stack}
                        </pre>
                      </>
                    )}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <Button
              appearance="secondary"
              onClick={handleClick77}
              type="button"
            >
              Back to Lobby
            </Button>
            <Button
              appearance="primary"
              disabled={isRetrying}
              onClick={onRetry}
              type="button"
            >
              {isRetrying ? (
                <>
                  <Spinner size="tiny" /> Retrying...
                </>
              ) : (
                "Retry"
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
